import { useEffect } from 'react'
import type { SupportedLanguage } from '../../i18n/routes'

const siteOrigin = 'https://ts-auto.it'
const defaultSocialImage = '/media/social/ts-auto-share.jpg'
const socialImageAlt = 'TS Auto'

type StructuredData = Record<string, unknown>

type RouteMetadata = {
  title: string
  description: string
  pathname: string
  image?: string
  language: SupportedLanguage
  structuredData?: StructuredData
}

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  const existed = Boolean(element)
  const previousAttributes = element
    ? Object.fromEntries(Object.keys(attributes).map((name) => [name, element?.getAttribute(name)]))
    : {}
  if (!element) {
    element = document.createElement('meta')
    document.head.append(element)
  }
  Object.entries(attributes).forEach(([name, value]) => element?.setAttribute(name, value))
  return { element, existed, previousAttributes }
}

type MetaRecord = ReturnType<typeof upsertMeta> & {
  removed?: boolean
  nextSibling?: ChildNode | null
}

function removeMeta(selector: string): MetaRecord | null {
  const element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) return null
  const nextSibling = element.nextSibling
  element.remove()
  return { element, existed: true, previousAttributes: {}, removed: true, nextSibling }
}

function restoreMeta(record: MetaRecord | null) {
  if (!record) return
  if (record.removed) {
    if (record.nextSibling?.parentNode === document.head) document.head.insertBefore(record.element, record.nextSibling)
    else document.head.append(record.element)
    return
  }
  if (!record.existed) {
    record.element.remove()
    return
  }
  Object.entries(record.previousAttributes).forEach(([name, value]) => {
    if (value === null || typeof value === 'undefined') record.element.removeAttribute(name)
    else record.element.setAttribute(name, value)
  })
}

export function useRouteMetadata({ title, description, pathname, image, language, structuredData }: RouteMetadata) {
  useEffect(() => {
    const usesDefaultSocialImage = !image
    const resolvedSocialImage = new URL(image ?? defaultSocialImage, __SITE_ORIGIN__).href
    const previousTitle = document.title
    const canonicalSelector = 'link[rel="canonical"]'
    let canonical = document.head.querySelector<HTMLLinkElement>(canonicalSelector)
    const canonicalWasCreated = !canonical
    const previousCanonical = canonical?.getAttribute('href') ?? null
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.append(canonical)
    }

    document.title = title
    canonical.href = new URL(pathname, siteOrigin).href
    const descriptionMeta = upsertMeta('meta[name="description"]', { name: 'description', content: description })
    const ogTitle = upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title })
    const ogDescription = upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description })
    const ogType = upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' })
    const ogImage = upsertMeta('meta[property="og:image"]', { property: 'og:image', content: resolvedSocialImage })
    const ogImageWidth = usesDefaultSocialImage
      ? upsertMeta('meta[property="og:image:width"]', { property: 'og:image:width', content: '1200' })
      : removeMeta('meta[property="og:image:width"]')
    const ogImageHeight = usesDefaultSocialImage
      ? upsertMeta('meta[property="og:image:height"]', { property: 'og:image:height', content: '630' })
      : removeMeta('meta[property="og:image:height"]')
    const ogImageType = usesDefaultSocialImage
      ? upsertMeta('meta[property="og:image:type"]', { property: 'og:image:type', content: 'image/jpeg' })
      : removeMeta('meta[property="og:image:type"]')
    const ogImageAlt = upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: socialImageAlt })
    const ogUrl = upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical.href })
    const ogSiteName = upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'TS Auto' })
    const ogLocale = upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: language === 'it' ? 'it_IT' : 'en_GB' })
    const twitterCard = upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' })
    const twitterTitle = upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title })
    const twitterDescription = upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description })
    const twitterImage = upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: resolvedSocialImage })
    const twitterImageAlt = upsertMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: socialImageAlt })
    const robots = upsertMeta('meta[name="robots"]', {
      name: 'robots',
      // The deployment stays non-indexable until the release flag is supplied.
      // English is a draft in every environment.
      content: import.meta.env.VITE_INDEXABLE === 'true' && language === 'it' ? 'index, follow' : 'noindex, nofollow',
    })

    const alternateSelector = 'link[rel="alternate"][hreflang]'
    let alternate = document.head.querySelector<HTMLLinkElement>(alternateSelector)
    const alternateWasCreated = !alternate
    const previousAlternateHref = alternate?.getAttribute('href') ?? null
    const previousAlternateLanguage = alternate?.getAttribute('hreflang') ?? null
    if (!alternate) {
      alternate = document.createElement('link')
      alternate.rel = 'alternate'
      document.head.append(alternate)
    }
    // Italian is the only locale approved for publication.
    alternate.hreflang = 'it'
    alternate.href = new URL(pathname.replace(/^\/en(?=\/|$)/, '/it'), siteOrigin).href

    let structuredDataElement: HTMLScriptElement | null = null
    if (structuredData) {
      structuredDataElement = document.createElement('script')
      structuredDataElement.type = 'application/ld+json'
      structuredDataElement.dataset.routeStructuredData = 'true'
      structuredDataElement.text = JSON.stringify(structuredData).replace(/</g, '\\u003c')
      document.head.append(structuredDataElement)
    }

    return () => {
      document.title = previousTitle
      if (canonicalWasCreated) canonical?.remove()
      else if (previousCanonical === null) canonical?.removeAttribute('href')
      else canonical?.setAttribute('href', previousCanonical)
      restoreMeta(descriptionMeta)
      restoreMeta(ogTitle)
      restoreMeta(ogDescription)
      restoreMeta(ogType)
      restoreMeta(ogImage)
      restoreMeta(ogImageWidth)
      restoreMeta(ogImageHeight)
      restoreMeta(ogImageType)
      restoreMeta(ogImageAlt)
      restoreMeta(ogUrl)
      restoreMeta(ogSiteName)
      restoreMeta(ogLocale)
      restoreMeta(twitterCard)
      restoreMeta(twitterTitle)
      restoreMeta(twitterDescription)
      restoreMeta(twitterImage)
      restoreMeta(twitterImageAlt)
      restoreMeta(robots)
      if (alternateWasCreated) alternate?.remove()
      else {
        if (previousAlternateHref === null) alternate?.removeAttribute('href')
        else alternate?.setAttribute('href', previousAlternateHref)
        if (previousAlternateLanguage === null) alternate?.removeAttribute('hreflang')
        else alternate?.setAttribute('hreflang', previousAlternateLanguage)
      }
      structuredDataElement?.remove()
    }
  }, [description, image, language, pathname, structuredData, title])
}
